// The template grammar of each store envelope (journey steps blob, tool revision, surface revision, session-repair
// entry). Store parses read their envelope through one function here: an unmarked (grammar 1) envelope is upgraded
// and stamped, a grammar 2 envelope is read as is, and any other grammar is refused, never misread or stripped.
//
// Leaf: imports only the template modules and the v1 upgrader. Shared by the runners, app tools, surfaces and server code.
import { upgradeJourneyBlobV1, upgradeSessionRepairEntryV1, upgradeSurfaceRevisionV1, upgradeToolV1 } from './variable-grammar-v1.heuristics.ts'
import { TemplateSyntaxError } from './template.ts'

/** The grammar this code reads and writes. An unmarked envelope is grammar 1. */
export const TEMPLATE_GRAMMAR = 2

/** The marker writers spread into an envelope. */
export const TEMPLATE_GRAMMAR_MARK = { grammar: TEMPLATE_GRAMMAR } as const

/** Whether an envelope carries a grammar marker (any value: unsupported ones are refused on read). */
export function isTemplateMarked(envelope: unknown): envelope is { grammar: unknown; [key: string]: unknown } {
  return !!envelope && typeof envelope === 'object' && 'grammar' in envelope && envelope.grammar !== undefined
}

export class UnsupportedTemplateGrammarError extends Error {
  constructor(envelope: string, grammar: unknown) {
    super(`The ${envelope} uses template grammar ${JSON.stringify(grammar)}; this build reads grammars 1 and ${TEMPLATE_GRAMMAR} only`)
    this.name = 'UnsupportedTemplateGrammarError'
  }
}

type Envelope = { readonly grammar?: unknown }

// Whether the envelope is unmarked (grammar 1); throws for a grammar this build does not implement.
function isV1(envelope: string, value: Envelope): boolean {
  if (value.grammar !== undefined && value.grammar !== TEMPLATE_GRAMMAR) throw new UnsupportedTemplateGrammarError(envelope, value.grammar)
  return value.grammar === undefined
}

type ErrorClass = abstract new (...args: never[]) => Error

/** `read()`, or `fallback` when its text is not a template this build reads (or the error is `unreadable`); any other error is rethrown. */
export function readTemplatesOr<T, F>(read: () => T, fallback: F, unreadable: readonly ErrorClass[] = []): T | F {
  try { return read() } catch (error) {
    if ([UnsupportedTemplateGrammarError, TemplateSyntaxError, ...unreadable].some(type => error instanceof type)) return fallback
    throw error
  }
}

/** An in-memory envelope, marked with the grammar its text holds before a store parse or a write. */
export function markTemplates<T>(value: T): T {
  return { ...value, ...TEMPLATE_GRAMMAR_MARK }
}

/** A stored journey steps blob in grammar 2. */
export function readJourneyTemplates<T extends Envelope & { readonly actions?: unknown; readonly variables?: unknown }>(blob: T): T {
  return isV1('journey steps blob', blob) ? markTemplates(upgradeJourneyBlobV1(blob)) : blob
}

/** A stored tool with every revision in grammar 2. One upgrade scope per tool (variable-grammar-v1.heuristics.ts). */
export function readToolTemplates<T extends { readonly revisions: readonly (Envelope & { readonly steps: { readonly actions: unknown } })[] }>(tool: T): T {
  if (!tool.revisions.map(revision => isV1('tool revision', revision)).includes(true)) return tool
  const upgraded = upgradeToolV1(tool)
  return { ...upgraded, revisions: upgraded.revisions.map(markTemplates) }
}

/** A stored surface revision in grammar 2. */
export function readSurfaceRevisionTemplates<T extends Envelope & { readonly steps: { readonly actions: unknown } }>(revision: T): T {
  return isV1('surface revision', revision) ? markTemplates(upgradeSurfaceRevisionV1(revision)) : revision
}

const reportSkippedRepair = (step: string, reason: string): void =>
  console.warn('[session-repairs] Skipping stored repair', JSON.stringify({ step, reason }))

/** A stored session-repair map in grammar 2. An entry no exact translation decides is skipped and reported. */
export function readSessionRepairTemplates<T extends Envelope & { readonly actions: unknown }>(entries: Readonly<Record<string, T>>,
  report = reportSkippedRepair): Record<string, T> {
  return Object.fromEntries(Object.entries(entries).flatMap(([step, entry]) => {
    if (!isV1('session repair', entry)) return [[step, entry]]
    const upgraded = upgradeSessionRepairEntryV1(entry)
    if (upgraded.kind === 'upgraded') return [[step, markTemplates(upgraded.entry)]]
    report(step, upgraded.reason)
    return []
  }))
}
